import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './AdminPlanPermissionsPage.css';
import { isPlatformAdminEmail } from '../constants/platformAdmin';
import { getSession } from '../services/authService';
import {
  isPlusPlanKey,
  listPlanPermissions,
  normalizePlanPermission,
  normalizePlanRowToTable,
  planItemPatch,
  planRowItemOn,
  plusPermissionDifferences,
  PLUS_CANONICAL_PLAN_KEY,
  PLUS_PLAN_KEYS,
  savePlanPermissions,
  STORAGE_LIMIT_MAX_BYTES,
  STORAGE_LIMIT_MIN_BYTES,
  unifyPlusPermissions,
  type PlanPermissionRow,
} from '../services/planPermissionsService';
import { formatResetDate, kstPeriodNow, notifyAiQuotaChanged } from '../services/aiQuotaService';
import { PLAN_AI_FEATURE_KEYS, PLAN_FEATURE_KEYS } from '../constants/planFeatures';
import { PLAN_TABLE_GROUPS, PLAN_TABLE_TEAM_ITEM, type PlanTableItem } from '../constants/planTableItems';
import { notifyPlanPermissionsChanged } from '../hooks/usePlanGateRows';
import { ACCOUNT_PASS_CATEGORY_PARTS, type AccountPassCategory } from '../utils/subscriptionPassStorage';

/** 화면 열: FREE 이용권 / PLUS 이용권(free 가 아닌 모든 분류 = 같은 권한) */
type ColumnKey = 'free' | 'plus';
const COLUMN_LABEL: Record<ColumnKey, string> = { free: 'FREE 이용권', plus: 'PLUS 이용권' };
/** 「베타 테스터 무료·이벤트 무료·…」(분류 이름에서 「PLUS - 」 접두어 제외) */
const PLUS_MEMBERS_TEXT = PLUS_PLAN_KEYS.map((k) => ACCOUNT_PASS_CATEGORY_PARTS[k].detail).join('·');
const columnOf = (k: AccountPassCategory): ColumnKey => (isPlusPlanKey(k) ? 'plus' : 'free');
const columnRow = (list: PlanPermissionRow[], c: ColumnKey): PlanPermissionRow | undefined =>
  c === 'free' ? list.find((r) => r.plan_key === 'free') : list.find((r) => r.plan_key === PLUS_CANONICAL_PLAN_KEY) ?? list.find((r) => isPlusPlanKey(r.plan_key));

/** 읽은 행 → 편집용(PLUS 행 통일 + 비교표 항목별로 묶인 키 정리) */
function toEditable(list: PlanPermissionRow[]): PlanPermissionRow[] {
  return unifyPlusPermissions(list).map(normalizePlanRowToTable);
}

/** 실측 평균(Haiku 5.5): 학습계획 ≈ 9.6 + 활동지 ≈ 12.0 크레딧 */
const CREDITS_PER_SET = 21.6;
const MB = 1048576;
const STORAGE_MB_MIN = Math.round(STORAGE_LIMIT_MIN_BYTES / MB);
const STORAGE_MB_MAX = Math.round(STORAGE_LIMIT_MAX_BYTES / MB);
const KRW_PER_USD = 1400;

function snapshot(rows: PlanPermissionRow[]): string {
  return JSON.stringify(
    rows.map((r) => {
      const n = normalizePlanPermission(r);
      return [
        n.plan_key,
        n.ai_learning_plan,
        n.ai_worksheet,
        n.ai_slides,
        n.ai_other,
        n.ai_monthly_credits,
        n.max_classes,
        n.storage_limit_bytes,
        PLAN_FEATURE_KEYS.map((k) => n.features[k]),
        // 실제 적용 값으로 비교(값이 없으면 ai_other) → 정리하며 명시 값을 채워도 변경으로 보지 않음
        PLAN_AI_FEATURE_KEYS.map((k) => n.ai_features[k] ?? n.ai_other),
      ];
    })
  );
}

const AdminPlanPermissionsPage: React.FC = () => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<PlanPermissionRow[]>([]);
  const [saved, setSaved] = useState<PlanPermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** 읽은 값이 PLUS 통일·항목 정리로 바뀌었으면 안내 */
  const [unifyNotice, setUnifyNotice] = useState<string | null>(null);

  const applyFresh = useCallback((fresh: PlanPermissionRow[]) => {
    const editable = toEditable(fresh);
    setSaved(fresh);
    setRows(editable);
    const diffs = plusPermissionDifferences(fresh);
    const reshaped = snapshot(editable) !== snapshot(fresh);
    setUnifyNotice(
      diffs.length
        ? `PLUS 계열 이용권(${PLUS_MEMBERS_TEXT})의 저장값이 서로 달라(${diffs.join(', ')}) 가장 넓은 권한으로 합쳐 표시했습니다. 저장하면 모두 같은 값으로 통일됩니다.`
        : reshaped
          ? '한 항목에 묶인 설정 중 일부만 켜져 있던 값을 「모두 켜져 있을 때만 사용」 기준으로 정리해 표시했습니다. 저장하면 이 값으로 통일됩니다.'
          : null
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const session = await getSession();
        if (!cancelled) setIsAdmin(isPlatformAdminEmail(session?.user?.email ?? ''));
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyFresh(await listPlanPermissions());
    } catch (err) {
      console.error(err);
      setError('이용권별 권한을 불러오지 못했습니다. 마이그레이션(20261026190000_plan_permissions_ai_quota.sql) 적용 여부를 확인해 주세요.');
    } finally {
      setLoading(false);
    }
  }, [applyFresh]);

  useEffect(() => {
    if (isAdmin) void reload();
    else if (isAdmin === false) setLoading(false);
  }, [isAdmin, reload]);

  const isDirty = useMemo(() => rows.length > 0 && snapshot(rows) !== snapshot(saved), [rows, saved]);

  /** 열 단위 수정. PLUS 열은 PLUS 계열 행 전부에 같은 값을 쓴다 */
  const patch = (col: ColumnKey, next: Partial<PlanPermissionRow>) => {
    setNotice(null);
    setRows((prev) => prev.map((r) => (columnOf(r.plan_key) === col ? { ...r, ...next } : r)));
  };

  const handleSave = async () => {
    if (!isDirty || saving) return;
    const before = new Map(saved.map((r) => [r.plan_key, snapshot([r])]));
    const differs = (r: PlanPermissionRow) => before.get(r.plan_key) !== snapshot([r]);
    // PLUS 계열은 하나라도 바뀌면 전부 함께 저장(행 값이 항상 같도록)
    const plusChanged = rows.some((r) => isPlusPlanKey(r.plan_key) && differs(r));
    const changed = rows.filter((r) => (isPlusPlanKey(r.plan_key) ? plusChanged : differs(r))).map(normalizePlanPermission);
    try {
      setSaving(true);
      setError(null);
      await savePlanPermissions(changed);
      applyFresh(await listPlanPermissions());
      const cols = [changed.some((r) => r.plan_key === 'free') ? 'FREE' : null, plusChanged ? 'PLUS' : null].filter(Boolean).join('·');
      setNotice(
        `${cols} 이용권 권한을 저장했습니다. 「이용권 구매 및 관리」 비교표에 바로 반영되며, AI·리모트·저장 용량은 서버에서도 바로, 다른 기본 기능은 이용자가 화면을 새로 열면 적용됩니다.`
      );
      notifyAiQuotaChanged();
      notifyPlanPermissionsChanged();
    } catch (err) {
      console.error(err);
      setError('저장에 실패했습니다. 관리자 계정과 마이그레이션 적용 여부를 확인해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  const period = kstPeriodNow();

  if (isAdmin === null || (isAdmin && loading && !rows.length)) {
    return (
      <section className="cp-admin-notices-page cp-admin-notices-page--edu">
        <p className="cp-dashboard-placeholder" role="status">
          로딩 중…
        </p>
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <section className="cp-admin-notices-page cp-admin-notices-page--edu">
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 관리자만 이용할 수 있습니다.
        </p>
      </section>
    );
  }

  type Column = { key: ColumnKey; row: PlanPermissionRow; prev: PlanPermissionRow | undefined };
  const columns: Column[] = (['free', 'plus'] as ColumnKey[]).flatMap((key) => {
    const row = columnRow(rows, key);
    return row ? [{ key, row, prev: columnRow(saved, key) }] : [];
  });

  const renderSwitch = (col: ColumnKey, label: string, on: boolean, changed: boolean, toggle: () => void) => (
    <td key={col} className={changed ? 'is-changed' : undefined}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`${COLUMN_LABEL[col]} ${label}`}
        className={`cp-admin-plan-perms-switch${on ? ' is-on' : ''}`}
        onClick={toggle}
      >
        <span className="cp-admin-plan-perms-switch-knob" aria-hidden />
        <span className="cp-admin-plan-perms-switch-text">{on ? '사용' : '끔'}</span>
      </button>
    </td>
  );

  /** 비교표 항목 한 줄(묶인 키 전체를 한 스위치로) */
  const renderItemRow = (item: PlanTableItem) => (
    <tr key={item.id}>
      <th scope="row">
        <span className="cp-admin-plan-perms-feature">{item.label}</span>
        <span className="cp-admin-plan-perms-hint">{item.hint}</span>
      </th>
      {columns.map(({ key, row, prev }) => {
        const on = planRowItemOn(item, row);
        const changed = prev ? planRowItemOn(item, prev) !== on : false;
        return renderSwitch(key, item.label, on, changed, () => patch(key, planItemPatch(item, row, !on)));
      })}
    </tr>
  );

  return (
    <section
      className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-plan-perms-page"
      aria-labelledby="cp-admin-plan-perms-title"
    >
      <div className="cp-page-title-row cp-admin-notices-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-admin-plan-perms-title">이용권별 권한 관리</h2>
          <p className="cp-admin-legal-subtitle">
            「이용권 구매 및 관리」 비교표와 같은 항목·순서로 FREE·PLUS 이용권의 기능 사용 여부와 클래스 등록 상한·저장용량·월 AI 크레딧을 정합니다. 저장한 값이 비교표의 ○/✕ 와 숫자에 그대로 보입니다. PLUS 이용권({PLUS_MEMBERS_TEXT})은 모두 같은 권한을 가집니다. AI 기능·저장용량·리모트는 서버에서도 같은 기준으로 막습니다.
          </p>
        </div>
        <div className="cp-annual-top-actions-save cp-admin-learning-top-right-actions cp-page-save-actions--chunky" role="group" aria-label="저장">
          <button
            type="button"
            className="cp-btn-save-all cp-admin-learning-top-save-btn"
            onClick={() => void handleSave()}
            disabled={!isDirty || saving}
            title={isDirty ? '저장하기' : '저장할 변경사항 없음'}
          >
            {saving ? '저장 중…' : isDirty ? '저장하기' : '변경사항 없음'}
          </button>
        </div>
      </div>

      {error ? (
        <p className="cp-dashboard-placeholder" role="alert">
          {error}
        </p>
      ) : null}
      {unifyNotice ? (
        <p className="cp-admin-plan-perms-notice" role="status">
          {unifyNotice}
        </p>
      ) : null}
      {notice ? (
        <p className="cp-admin-plan-perms-notice" role="status">
          {notice}
        </p>
      ) : null}

      <div className="cp-dashboard-block cp-admin-plan-perms-card">
        <div className="cp-admin-plan-perms-table-wrap">
          <table className="cp-admin-plan-perms-table">
            <thead>
              <tr>
                <th scope="col" className="cp-admin-plan-perms-corner">
                  기능
                </th>
                {columns.map(({ key }) => (
                  <th key={key} scope="col" className="cp-admin-plan-perms-col">
                    <span className={`cp-sub-pass-pill ${key === 'free' ? 'cp-sub-pass-pill--muted' : 'cp-sub-pass-pill--accent'}`}>
                      {COLUMN_LABEL[key]}
                    </span>
                    <span className="cp-admin-plan-perms-plan-name">
                      {key === 'free' ? '무료 이용자' : PLUS_MEMBERS_TEXT}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="cp-admin-plan-perms-group">
                <th scope="rowgroup" colSpan={columns.length + 1}>
                  기본 설정
                </th>
              </tr>
              <tr>
                <th scope="row">
                  <span className="cp-admin-plan-perms-feature">클래스 등록 상한</span>
                  <span className="cp-admin-plan-perms-hint">학급 설정에서 추가할 수 있는 클래스(학급) 수</span>
                </th>
                {columns.map(({ key, row: r, prev }) => {
                  const changed = prev?.max_classes !== r.max_classes;
                  return (
                    <td key={key} className={changed ? 'is-changed' : undefined}>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        inputMode="numeric"
                        className="cp-admin-plan-perms-num cp-admin-plan-perms-num--short"
                        value={r.max_classes}
                        onChange={(e) => patch(key, { max_classes: Math.min(100, Math.max(1, Math.round(Number(e.target.value) || 1))) })}
                        aria-label={`${COLUMN_LABEL[key]} 클래스 등록 상한`}
                      />
                      <span className="cp-admin-plan-perms-sub">개</span>
                    </td>
                  );
                })}
              </tr>
              {renderItemRow(PLAN_TABLE_TEAM_ITEM)}
              <tr>
                <th scope="row">
                  <span className="cp-admin-plan-perms-feature">저장용량 (MB)</span>
                  <span className="cp-admin-plan-perms-hint">모든 이미지 합산 한도 · 넘으면 새 업로드만 막힘(기존 이미지는 유지)</span>
                </th>
                {columns.map(({ key, row: r, prev }) => {
                  const bytes = r.storage_limit_bytes;
                  if (bytes === null) {
                    return (
                      <td key={key}>
                        <span className="cp-admin-plan-perms-sub">100 MB (마이그레이션 20261027190000 적용 후 변경 가능)</span>
                      </td>
                    );
                  }
                  const changed = prev?.storage_limit_bytes !== bytes;
                  return (
                    <td key={key} className={changed ? 'is-changed' : undefined}>
                      <input
                        type="number"
                        min={STORAGE_MB_MIN}
                        max={STORAGE_MB_MAX}
                        step={1}
                        inputMode="numeric"
                        className="cp-admin-plan-perms-num cp-admin-plan-perms-num--short"
                        value={Math.round(bytes / MB)}
                        onChange={(e) => {
                          const mb = Math.min(STORAGE_MB_MAX, Math.max(STORAGE_MB_MIN, Math.round(Number(e.target.value) || STORAGE_MB_MIN)));
                          patch(key, { storage_limit_bytes: mb * MB });
                        }}
                        aria-label={`${COLUMN_LABEL[key]} 저장용량(MB)`}
                      />
                      <span className="cp-admin-plan-perms-sub">MB</span>
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th scope="row">
                  <span className="cp-admin-plan-perms-feature">월 AI 크레딧</span>
                  <span className="cp-admin-plan-perms-hint">1 크레딧 = 원가 $0.0001 · 매월 1일 0시(KST) 초기화</span>
                </th>
                {columns.map(({ key, row: r, prev }) => {
                  const changed = prev?.ai_monthly_credits !== r.ai_monthly_credits;
                  const krw = Math.round(r.ai_monthly_credits * 0.0001 * KRW_PER_USD);
                  return (
                    <td key={key} className={changed ? 'is-changed' : undefined}>
                      <input
                        type="number"
                        min={0}
                        max={10000000}
                        step={100}
                        inputMode="numeric"
                        className="cp-admin-plan-perms-num"
                        value={Number.isFinite(r.ai_monthly_credits) ? r.ai_monthly_credits : 0}
                        onChange={(e) => patch(key, { ai_monthly_credits: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                        aria-label={`${COLUMN_LABEL[key]} 월 AI 크레딧`}
                      />
                      <span className="cp-admin-plan-perms-sub">
                        ≈ {Math.floor(r.ai_monthly_credits / CREDITS_PER_SET).toLocaleString('ko-KR')}세트 · 최대 약 {krw.toLocaleString('ko-KR')}원
                      </span>
                    </td>
                  );
                })}
              </tr>
              {PLAN_TABLE_GROUPS.map((g) => (
                <React.Fragment key={g.title}>
                  <tr className="cp-admin-plan-perms-group">
                    <th scope="rowgroup" colSpan={columns.length + 1}>
                      {g.title}
                    </th>
                  </tr>
                  {g.items.map(renderItemRow)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="cp-admin-plan-perms-foot">
          플랫폼 관리자 계정은 이용권과 관계없이 모든 AI 기능을 제한 없이 쓰며, 사용 기록만 남습니다. 다음 초기화:{' '}
          {formatResetDate(period.resetAt)} 0시(한국 시간).
        </p>
      </div>
    </section>
  );
};

export default AdminPlanPermissionsPage;
